import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, FileText, Clock, CheckCircle, AlertCircle, AlertTriangle, Loader, Trash2, X, ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { httpClient } from '../services/httpClient';
import { Case, CaseStatus, CasePageResponse } from '../types';
import { formatDistanceToNow } from 'date-fns';
import { caseApi } from '../services/api';
import { useOpenCases } from '../contexts/OpenCasesContext';
import { RedactionText } from '../components/redaction/RedactionText';

type CaseSortKey = 'name' | 'patientName' | 'status' | 'documentCount' | 'lastActivity' | 'createdAt';
type SortDirection = 'asc' | 'desc';

const COLUMNS: { key: CaseSortKey | 'caseType' | 'actions'; label: string; sortable: boolean }[] = [
  { key: 'name', label: 'Case Name', sortable: true },
  { key: 'patientName', label: 'Patient', sortable: true },
  { key: 'patientIdentifier' as any, label: 'Patient No.', sortable: false },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'documentCount', label: 'Docs', sortable: true },
  { key: 'caseType', label: 'Type', sortable: false },
  { key: 'lastActivity', label: 'Last Activity', sortable: true },
  { key: 'createdAt', label: 'Created', sortable: true },
  { key: 'actions', label: '', sortable: false },
];

export const CaseListPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<CaseStatus | 'all'>('all');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [caseToDelete, setCaseToDelete] = useState<Case | null>(null);
  const [confirmationText, setConfirmationText] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<CaseSortKey>('lastActivity');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { openCases, openCase, closeCase } = useOpenCases();

  const toggleSort = (key: CaseSortKey) => {
    if (sortBy === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
    setCurrentPage(0);
  };

  // One-click case creation
  const nextUntitledName = useCallback(() => {
    const existing = openCases.filter(t => /^Untitled Case( \d+)?$/.test(t.name));
    if (existing.length === 0) return 'Untitled Case';
    const nums = existing.map(t => {
      const m = t.name.match(/^Untitled Case (\d+)$/);
      return m ? parseInt(m[1], 10) : 1;
    });
    return `Untitled Case ${Math.max(...nums) + 1}`;
  }, [openCases]);

  const createCaseMutation = useMutation(
    (name: string) => caseApi.createCase({ name }),
    {
      onSuccess: (data, name) => {
        openCase(data.caseId, name);
        navigate(`/cases/${data.caseId}/records`);
      },
      onError: () => {
        setCreateError('Failed to create case. Please try again.');
        setTimeout(() => setCreateError(null), 4000);
      },
    }
  );

  const { data, isLoading, error, refetch } = useQuery(
    ['cases', searchTerm, statusFilter, sortBy, sortDir, currentPage, pageSize],
    async () => {
      const params: Record<string, any> = {
        page: currentPage,
        size: pageSize,
        sortBy,
        sortDir,
      };
      if (searchTerm) params.search = searchTerm;
      if (statusFilter !== 'all') params.status = statusFilter;

      const response = await httpClient.get<CasePageResponse>('/cases', params);

      // Fetch actual document counts and chronology status for each case
      const casesWithRealStatus = await Promise.all(
        response.content.map(async (caseItem) => {
          try {
            // Get documents
            const docs = await httpClient.get<any>(`/documents/cases/${caseItem.id}/documents`);
            const actualDocCount = Array.isArray(docs) ? docs.length : 0;

            // Check if chronology exists (indicates processing is complete)
            let hasChronology = false;
            try {
              const chronology = await httpClient.get<any>(`/chronologies/case/${caseItem.id}`);
              hasChronology = chronology && chronology.events && chronology.events.length > 0;
            } catch (err: any) {
              // 404 is expected for cases without chronology
              // Non-404 errors indicate a service problem — log them
              if (err?.response?.status && err.response.status !== 404) {
                console.error(`Chronology service error for case ${caseItem.id} (HTTP ${err.response.status}):`, err);
              }
              hasChronology = false;
            }

            // Check if metadata extraction is actively running
            let isAnalysing = false;
            try {
              const progress = await httpClient.get<{ complete: boolean; failed: boolean }>(
                `/intelligence/metadata/progress/${caseItem.id}`
              );
              if (progress && !progress.complete && !progress.failed) {
                isAnalysing = true;
              }
            } catch (err: any) {
              // 204/404 is expected when no extraction has run
              if (err?.response?.status && err.response.status !== 404 && err.response.status !== 204) {
                console.error(`Metadata progress error for case ${caseItem.id} (HTTP ${err.response.status}):`, err);
              }
            }

            return {
              ...caseItem,
              documentCount: actualDocCount,
              hasChronology,
              isAnalysing
            };
          } catch (err) {
            console.error(`Failed to get status for case ${caseItem.id}:`, err);
            return caseItem;
          }
        })
      );

      return { ...response, content: casesWithRealStatus };
    },
    {
      keepPreviousData: true,
      // Only auto-refresh if there are cases actively processing or analysing
      refetchInterval: (data) => {
        const cases = data?.content || [];
        const hasActiveProcessing = cases.some(c =>
          c.status === 'PROCESSING' || c.status === 'UPLOADING' || (c as any).isAnalysing
        );
        // Poll every 10 seconds if processing/analysing, otherwise don't poll
        return hasActiveProcessing ? 10000 : false;
      },
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    (caseId: string) => caseApi.deleteCase(caseId),
    {
      onSuccess: (_data, caseId) => {
        closeCase(caseId);
        queryClient.invalidateQueries(['cases']);
        setDeleteModalOpen(false);
        setCaseToDelete(null);
        setConfirmationText('');
      },
      onError: (error) => {
        console.error('Failed to delete case:', error);
        alert('Failed to delete case. Please try again.');
      }
    }
  );

  const handleDeleteClick = (e: React.MouseEvent, caseItem: Case) => {
    e.preventDefault(); // Prevent navigation
    e.stopPropagation();
    if (caseItem.documentCount === 0) {
      deleteMutation.mutate(caseItem.id);
      return;
    }
    setCaseToDelete(caseItem);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (caseToDelete && confirmationText === caseToDelete.name) {
      deleteMutation.mutate(caseToDelete.id);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setCaseToDelete(null);
    setConfirmationText('');
  };

  // Compute display status based on actual case state
  const getDisplayStatus = (caseItem: any): string => {
    // If backend status is already set to something other than CREATED, use it
    if (caseItem.status !== 'CREATED' && caseItem.status !== 'QUEUED') {
      return caseItem.status;
    }

    // Compute status based on actual data
    if (caseItem.documentCount === 0) {
      return 'CREATED'; // No documents
    } else if (caseItem.isAnalysing) {
      return 'ANALYSING'; // Analysis is actively running (check before chronology — chronology exists before cite phase completes)
    } else if (caseItem.hasChronology) {
      return 'PROCESSED'; // Has documents and chronology, analysis fully complete
    } else {
      return 'READY'; // Has documents but not processed yet
    }
  };

  const getStatusIcon = (status: string) => {
    const normalizedStatus = status.toUpperCase();
    switch (normalizedStatus) {
      case 'CREATED':
        return <FileText className="h-4 w-4 text-gray-500" />;
      case 'UPLOADING':
        return <Loader className="h-4 w-4 text-cyan-500 animate-spin" />;
      case 'PROCESSING':
        return <Clock className="h-4 w-4 text-yellow-500 animate-pulse" />;
      case 'QUEUED':
        return <Clock className="h-4 w-4 text-orange-500" />;
      case 'READY':
        return <CheckCircle className="h-4 w-4 text-cyan-500" />;
      case 'ANALYSING':
        return <Loader className="h-4 w-4 text-purple-500 animate-spin" />;
      case 'PARTIAL':
        return <AlertTriangle className="h-4 w-4 text-amber-500" />;
      case 'PROCESSED':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'REVIEW':
        return <CheckCircle className="h-4 w-4 text-cyan-600" />;
      case 'COMPLETE':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'ERROR':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <FileText className="h-4 w-4 text-medical-400" />;
    }
  };

  const getStatusText = (status: string) => {
    const normalizedStatus = status.toUpperCase();
    switch (normalizedStatus) {
      case 'CREATED':
        return 'Created';
      case 'UPLOADING':
        return 'Uploading';
      case 'PROCESSING':
        return 'Processing';
      case 'QUEUED':
        return 'Queued';
      case 'READY':
        return 'Ready';
      case 'ANALYSING':
        return 'Analysing';
      case 'PARTIAL':
        return 'Partial';
      case 'PROCESSED':
        return 'Processed';
      case 'REVIEW':
        return 'In Review';
      case 'COMPLETE':
        return 'Complete';
      case 'ERROR':
        return 'Error';
      default:
        return 'Unknown';
    }
  };

  const getStatusColor = (status: string) => {
    const normalizedStatus = status.toUpperCase();
    switch (normalizedStatus) {
      case 'CREATED':
        return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
      case 'UPLOADING':
        return 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300';
      case 'PROCESSING':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'QUEUED':
        return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300';
      case 'READY':
        return 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300';
      case 'ANALYSING':
        return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300';
      case 'PARTIAL':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
      case 'PROCESSED':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
      case 'REVIEW':
        return 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300';
      case 'COMPLETE':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'ERROR':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
      default:
        return 'bg-medical-100 text-medical-800 dark:bg-medical-700 dark:text-medical-300';
    }
  };

  const SortIcon: React.FC<{ columnKey: string }> = ({ columnKey }) => {
    if (sortBy !== columnKey) {
      return <ChevronsUpDown className="h-3.5 w-3.5 text-medical-400" />;
    }
    return sortDir === 'asc'
      ? <ChevronUp className="h-3.5 w-3.5 text-primary-600" />
      : <ChevronDown className="h-3.5 w-3.5 text-primary-600" />;
  };

  if (isLoading && !data) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <Loader className="h-8 w-8 animate-spin text-primary-600" />
          <span className="ml-2 text-medical-600 dark:text-medical-400">Loading cases...</span>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="p-6">
        <div className="card">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-medical-900 dark:text-medical-100 mb-2">Error Loading Cases</h3>
            <p className="text-medical-600 dark:text-medical-400 mb-4">
              Unable to load cases. Please check your connection and try again.
            </p>
            <button
              onClick={() => refetch()}
              className="btn-primary"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const cases = data?.content || [];
  const totalElements = data?.totalElements ?? 0;
  const totalPages = data?.totalPages ?? 1;

  return (
    <div className="px-6 pt-4 pb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-medical-900 dark:text-medical-100">Cases</h1>
          <p className="text-medical-600 dark:text-medical-400">Manage your medico-legal case reviews</p>
        </div>
        <button
          onClick={() => createCaseMutation.mutate(nextUntitledName())}
          disabled={createCaseMutation.isLoading}
          className="btn-primary flex items-center space-x-2 mr-28"
        >
          {createCaseMutation.isLoading ? (
            <Loader className="h-4 w-4 animate-spin" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
          <span>{createCaseMutation.isLoading ? 'Creating...' : 'New Case'}</span>
        </button>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-medical-400 dark:text-medical-500" />
              <input
                type="text"
                placeholder="Search by name, patient, or description..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(0); }}
                className="input-field pl-10"
              />
            </div>
          </div>
          <div className="sm:w-48">
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value as CaseStatus | 'all'); setCurrentPage(0); }}
              className="input-field"
            >
              <option value="all">All Status</option>
              <option value="created">Created</option>
              <option value="uploading">Uploading</option>
              <option value="processing">Processing</option>
              <option value="ready">Ready</option>
              <option value="analysing">Analysing</option>
              <option value="processed">Processed</option>
              <option value="review">In Review</option>
              <option value="complete">Complete</option>
              <option value="error">Error</option>
            </select>
          </div>
        </div>
      </div>

      {/* Cases Table */}
      {cases.length === 0 && !isLoading ? (
        <div className="card">
          <div className="text-center py-12">
            <FileText className="h-16 w-16 text-medical-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-medical-900 dark:text-medical-100 mb-2">No Cases Found</h3>
            <p className="text-medical-600 dark:text-medical-400 mb-6">
              {searchTerm || statusFilter !== 'all'
                ? 'No cases match your current filters.'
                : 'Get started by creating your first case.'}
            </p>
            {!searchTerm && statusFilter === 'all' && (
              <button
                onClick={() => createCaseMutation.mutate(nextUntitledName())}
                disabled={createCaseMutation.isLoading}
                className="btn-primary"
              >
                {createCaseMutation.isLoading ? 'Creating...' : 'Create First Case'}
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="card" data-testid="case-list">
          {/* Table header bar */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-medical-500">
              {totalElements} {totalElements === 1 ? 'case' : 'cases'}
            </p>
            <div className="flex items-center gap-2">
              <span className="text-sm text-medical-500">Rows:</span>
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(0); }}
                className="input-field w-20 py-1 text-sm"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto -mx-6">
            <table className="min-w-full divide-y divide-medical-200 dark:divide-medical-700">
              <thead className="bg-medical-50 dark:bg-medical-900">
                <tr>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      onClick={() => col.sortable && toggleSort(col.key as CaseSortKey)}
                      className={`px-4 py-3 text-left text-xs font-medium text-medical-500 uppercase tracking-wider ${col.sortable ? 'cursor-pointer hover:text-medical-700 dark:hover:text-medical-300 select-none' : ''}`}
                    >
                      <div className="flex items-center gap-1">
                        <span>{col.label}</span>
                        {col.sortable && <SortIcon columnKey={col.key} />}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-medical-800 divide-y divide-medical-200 dark:divide-medical-700">
                {cases.map((caseItem: any) => {
                  const displayStatus = getDisplayStatus(caseItem);
                  return (
                    <tr
                      key={caseItem.id}
                      className="hover:bg-medical-50 dark:hover:bg-medical-700/50 transition-colors"
                    >
                      {/* Case Name */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Link
                          to={`/cases/${caseItem.id}`}
                          className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                        >
                          <RedactionText text={caseItem.name} />
                        </Link>
                      </td>

                      {/* Patient Name */}
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-medical-700 dark:text-medical-300">
                        {caseItem.patientName ? (
                          <RedactionText text={caseItem.patientName} />
                        ) : (
                          <span className="text-medical-400">&mdash;</span>
                        )}
                      </td>

                      {/* Patient Identifier */}
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-medical-700 dark:text-medical-300">
                        {caseItem.patientIdentifier ? (
                          <RedactionText text={caseItem.patientIdentifier} />
                        ) : (
                          <span className="text-medical-400">&mdash;</span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(displayStatus)}`}>
                          {getStatusIcon(displayStatus)}
                          {getStatusText(displayStatus)}
                        </span>
                      </td>

                      {/* Documents */}
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-medical-700 dark:text-medical-300">
                        <span className="flex items-center gap-1">
                          <FileText className="h-3.5 w-3.5 text-medical-400" />
                          {caseItem.documentCount}
                        </span>
                      </td>

                      {/* Case Type */}
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-medical-600 dark:text-medical-400">
                        {caseItem.caseTypes && caseItem.caseTypes.length > 0
                          ? caseItem.caseTypes.join(', ')
                          : <span className="text-medical-400">&mdash;</span>}
                      </td>

                      {/* Last Activity */}
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-medical-500 dark:text-medical-400">
                        {caseItem.lastActivity
                          ? formatDistanceToNow(new Date(caseItem.lastActivity), { addSuffix: true })
                          : <span className="text-medical-400">&mdash;</span>}
                      </td>

                      {/* Created */}
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-medical-500 dark:text-medical-400">
                        {formatDistanceToNow(new Date(caseItem.createdAt), { addSuffix: true })}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <button
                          onClick={(e) => handleDeleteClick(e, caseItem)}
                          className="p-1.5 text-gray-400 dark:text-medical-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Delete case"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-medical-200 dark:border-medical-700">
              <p className="text-sm text-medical-500">
                Page {currentPage + 1} of {totalPages}
              </p>
              <div className="flex gap-1">
                <button
                  disabled={currentPage === 0}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  className="p-2 rounded-lg text-medical-600 dark:text-medical-400 hover:bg-medical-100 dark:hover:bg-medical-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  disabled={currentPage >= totalPages - 1}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  className="p-2 rounded-lg text-medical-600 dark:text-medical-400 hover:bg-medical-100 dark:hover:bg-medical-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create Error Toast */}
      {createError && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-red-600 text-white px-4 py-3 rounded-lg shadow-lg text-sm flex items-center gap-2">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{createError}</span>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && caseToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-medical-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-medical-100">Delete Case</h3>
                  <p className="text-sm text-gray-600 dark:text-medical-400">This action cannot be undone</p>
                </div>
              </div>
              <button
                onClick={handleCancelDelete}
                className="text-gray-400 dark:text-medical-500 hover:text-gray-600 dark:hover:text-medical-300 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-gray-700 dark:text-medical-300 mb-4">
                You are about to permanently delete the case:
              </p>
              <div className="bg-gray-50 dark:bg-medical-700 border border-gray-200 dark:border-medical-600 rounded-lg p-3 mb-4">
                <p className="font-medium text-gray-900 dark:text-medical-100">{caseToDelete.name}</p>
                {caseToDelete.description && (
                  <p className="text-sm text-gray-600 dark:text-medical-400 mt-1">{caseToDelete.description}</p>
                )}
                <p className="text-sm text-gray-500 dark:text-medical-400 mt-2">
                  {caseToDelete.documentCount} document{caseToDelete.documentCount !== 1 ? 's' : ''}
                </p>
              </div>
              <p className="text-sm text-gray-700 dark:text-medical-300 mb-3">
                To confirm deletion, please type the case name exactly as shown above:
              </p>
              <input
                type="text"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                placeholder="Type case name here"
                className="w-full px-3 py-2 border border-gray-300 dark:border-medical-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent dark:bg-medical-700 dark:text-medical-100"
                autoFocus
              />
              {confirmationText && confirmationText !== caseToDelete.name && (
                <p className="text-sm text-red-600 mt-2">
                  Case name does not match
                </p>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleCancelDelete}
                disabled={deleteMutation.isLoading}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-medical-600 text-gray-700 dark:text-medical-300 rounded-lg hover:bg-gray-50 dark:hover:bg-medical-700 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={confirmationText !== caseToDelete.name || deleteMutation.isLoading}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {deleteMutation.isLoading ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    <span>Delete Case</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
